import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isAdmin } from "@/lib/admin.functions";
import {
  listAcademyStudents,
  listPendingStudents,
  approveStudent,
  listAdminCourses,
  saveAcademyCourse,
  deleteAcademyCourse,
  listCourseStructure,
  saveAcademyModule,
  deleteAcademyModule,
  saveAcademyLesson,
  deleteAcademyLesson,
  listAdminOffers,
  saveAcademyOffer,
  deleteAcademyOffer,
  listAdminHelpArticles,
  saveHelpArticle,
  deleteHelpArticle,
  listAdminTickets,
  updateAdminTicket,
  listAdminSubmissions,
  reviewSubmission,
} from "@/lib/academy.functions";

export const Route = createFileRoute("/admin/academia")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Academia — Admin | SVG Multimídia" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminAcademia,
});

type Tab = "cursos" | "aprovacoes" | "alunos" | "ofertas" | "ajuda" | "tickets" | "entregas";

function AdminAcademia() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [tab, setTab] = useState<Tab>("cursos");

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!alive) return;
      if (!data.user) {
        navigate({ to: "/auth", replace: true });
        return;
      }
      try {
        const r = await isAdmin();
        if (!alive) return;
        setAllowed(r.admin);
      } catch {
        setAllowed(false);
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [navigate]);

  if (checking) return <div className="container-svg py-24 text-center text-sm text-muted-foreground">Validando permissão…</div>;
  if (!allowed) return <div className="container-svg py-24 text-center text-sm text-destructive">Acesso restrito a administradores.</div>;

  return (
    <div className="container-svg py-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Academia SVG · Admin</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gerencie cursos, alunos, ofertas e suporte da plataforma educacional.</p>
        </div>
        <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground">← Voltar ao Admin geral</Link>
      </div>

      <nav className="mt-6 flex flex-wrap gap-2 border-b border-ink/12 pb-2">
        {(["cursos", "aprovacoes", "alunos", "ofertas", "ajuda", "tickets", "entregas"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-4 py-1.5 text-xs font-medium capitalize transition ${
              tab === t ? "bg-primary/20 text-foreground ring-1 ring-primary/40" : "text-muted-foreground hover:bg-ink/[0.04]"
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === "cursos" && <CoursesTab />}
        {tab === "aprovacoes" && <ApprovalsTab />}
        {tab === "alunos" && <StudentsTab />}
        {tab === "ofertas" && <OffersTab />}
        {tab === "ajuda" && <HelpTab />}
        {tab === "tickets" && <TicketsTab />}
        {tab === "entregas" && <SubmissionsTab />}
      </div>
    </div>
  );
}

function ApprovalsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["adm-academy", "pending"],
    queryFn: () => listPendingStudents(),
  });
  const act = useMutation({
    mutationFn: (v: { userId: string; approve: boolean; reason?: string }) => approveStudent({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy"] }),
  });
  return (
    <div className="card-glass">
      <h2 className="text-lg font-semibold">Cadastros aguardando aprovação</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Confirme o pagamento antes de aprovar. Alunos aprovados recebem acesso imediato à Academia.
      </p>
      {isLoading ? (
        <p className="mt-4 text-xs text-muted-foreground">Carregando…</p>
      ) : !data || data.length === 0 ? (
        <p className="mt-4 text-xs text-muted-foreground">Nenhum cadastro pendente.</p>
      ) : (
        <ul className="mt-4 divide-y divide-ink/12">
          {data.map((s: any) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
              <div>
                <div className="font-medium">{s.full_name || "(sem nome)"}</div>
                <div className="text-xs text-muted-foreground">
                  {s.phone || "sem telefone"} · {[s.city, s.state].filter(Boolean).join("/") || "sem cidade"} ·
                  criado em {new Date(s.created_at).toLocaleDateString("pt-BR")}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => act.mutate({ userId: s.user_id, approve: true })}
                  disabled={act.isPending}
                  className="btn-primary text-xs"
                >
                  Aprovar
                </button>
                <button
                  onClick={() => {
                    const reason = prompt("Motivo da rejeição (opcional):") ?? undefined;
                    act.mutate({ userId: s.user_id, approve: false, reason });
                  }}
                  disabled={act.isPending}
                  className="btn-outline text-xs text-destructive"
                >
                  Rejeitar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CoursesTab() {
  const qc = useQueryClient();
  const { data: courses } = useQuery({ queryKey: ["adm-academy", "courses"], queryFn: () => listAdminCourses() });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<any>({
    slug: "",
    title: "",
    description: "",
    cover_url: "",
    level: "iniciante",
    category: "",
    order_index: 0,
    published: false,
  });
  const save = useMutation({
    mutationFn: () => {
      const payload: any = { ...form };
      if (selectedId) payload.id = selectedId;
      if (!payload.cover_url) delete payload.cover_url;
      return saveAcademyCourse({ data: payload });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy", "courses"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteAcademyCourse({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy"] }),
  });

  function loadEdit(c: any) {
    setSelectedId(c.id);
    setForm({
      slug: c.slug,
      title: c.title,
      description: c.description ?? "",
      cover_url: c.cover_url ?? "",
      level: c.level ?? "iniciante",
      category: c.category ?? "",
      order_index: c.order_index ?? 0,
      published: !!c.published,
    });
  }
  function resetForm() {
    setSelectedId(null);
    setForm({ slug: "", title: "", description: "", cover_url: "", level: "iniciante", category: "", order_index: 0, published: false });
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
      <section className="card-glass">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Cursos</h2>
          <button onClick={resetForm} className="btn-outline text-xs">+ Novo</button>
        </div>
        <ul className="mt-3 divide-y divide-ink/12">
          {(courses ?? []).map((c: any) => (
            <li key={c.id} className="flex items-center justify-between py-2 text-sm">
              <button onClick={() => loadEdit(c)} className="text-left hover:text-primary">
                <div className="font-medium">{c.title}</div>
                <div className="text-xs text-muted-foreground">{c.slug} · {c.published ? "publicado" : "rascunho"}</div>
              </button>
              <button onClick={() => confirm("Excluir curso?") && del.mutate(c.id)} className="text-xs text-destructive">Excluir</button>
            </li>
          ))}
          {courses && courses.length === 0 && <li className="py-4 text-xs text-muted-foreground">Nenhum curso cadastrado.</li>}
        </ul>
      </section>

      <section className="card-glass space-y-3">
        <h2 className="text-lg font-semibold">{selectedId ? "Editar curso" : "Novo curso"}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs">Slug<input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="mt-1 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" /></label>
          <label className="text-xs">Ordem<input type="number" value={form.order_index} onChange={(e) => setForm({ ...form, order_index: Number(e.target.value) })} className="mt-1 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" /></label>
          <label className="text-xs sm:col-span-2">Título<input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" /></label>
          <label className="text-xs sm:col-span-2">Descrição<textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" /></label>
          <label className="text-xs">Categoria<input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" /></label>
          <label className="text-xs">Nível<input value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="mt-1 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" /></label>
          <label className="text-xs sm:col-span-2">Capa (URL)<input value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="mt-1 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" /></label>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publicado</label>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary text-sm">
            {save.isPending ? "Salvando…" : "Salvar"}
          </button>
          {save.error && <span className="text-xs text-destructive">{(save.error as Error).message}</span>}
        </div>
        {selectedId && <CourseStructureEditor courseId={selectedId} />}
      </section>
    </div>
  );
}

function CourseStructureEditor({ courseId }: { courseId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-academy", "structure", courseId], queryFn: () => listCourseStructure({ data: { courseId } }) });
  const [modTitle, setModTitle] = useState("");
  const addMod = useMutation({
    mutationFn: () =>
      saveAcademyModule({
        data: { course_id: courseId, title: modTitle, description: "", order_index: data?.modules.length ?? 0, published: true },
      }),
    onSuccess: () => {
      setModTitle("");
      qc.invalidateQueries({ queryKey: ["adm-academy", "structure", courseId] });
    },
  });
  const delMod = useMutation({
    mutationFn: (id: string) => deleteAcademyModule({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy", "structure", courseId] }),
  });

  return (
    <div className="mt-6 border-t border-ink/12 pt-4">
      <h3 className="text-sm font-semibold">Estrutura (módulos & aulas)</h3>
      <div className="mt-3 flex gap-2">
        <input value={modTitle} onChange={(e) => setModTitle(e.target.value)} placeholder="Título do módulo" className="flex-1 rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" />
        <button onClick={() => modTitle && addMod.mutate()} className="btn-outline text-xs">+ Módulo</button>
      </div>
      <div className="mt-4 space-y-4">
        {(data?.modules ?? []).map((m: any) => (
          <ModuleBlock key={m.id} module={m} courseId={courseId} onDelete={() => confirm("Excluir módulo?") && delMod.mutate(m.id)} />
        ))}
      </div>
    </div>
  );
}

function ModuleBlock({ module, courseId, onDelete }: { module: any; courseId: string; onDelete: () => void }) {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [showEditMod, setShowEditMod] = useState(false);
  const [modForm, setModForm] = useState<any>({
    title: module.title,
    description: module.description ?? "",
    summary: module.summary ?? "",
    video_url: module.video_url ?? "",
    cover_url: module.cover_url ?? "",
    order_index: module.order_index ?? 0,
    published: !!module.published,
    materials: Array.isArray(module.materials) ? module.materials : [],
  });
  const saveMod = useMutation({
    mutationFn: () => {
      const payload: any = { id: module.id, course_id: courseId, ...modForm };
      if (!payload.video_url) delete payload.video_url;
      if (!payload.cover_url) delete payload.cover_url;
      if (!payload.summary) delete payload.summary;
      return saveAcademyModule({ data: payload });
    },
    onSuccess: () => {
      setShowEditMod(false);
      qc.invalidateQueries({ queryKey: ["adm-academy", "structure", courseId] });
    },
  });
  const [lesson, setLesson] = useState<any>({
    slug: "",
    title: "",
    lesson_type: "video",
    video_url: "",
    pdf_url: "",
    content_md: "",
    duration_min: 0,
    points: 10,
    order_index: module.lessons.length,
    published: true,
    tools: [] as Array<{ name: string; url: string; description?: string }>,
  });
  const save = useMutation({
    mutationFn: () => {
      const payload: any = { ...lesson, module_id: module.id };
      if (!payload.video_url) delete payload.video_url;
      if (!payload.pdf_url) delete payload.pdf_url;
      return saveAcademyLesson({ data: payload });
    },
    onSuccess: () => {
      setShowAdd(false);
      setLesson({ slug: "", title: "", lesson_type: "video", video_url: "", pdf_url: "", content_md: "", duration_min: 0, points: 10, order_index: module.lessons.length + 1, published: true, tools: [] });
      qc.invalidateQueries({ queryKey: ["adm-academy", "structure", courseId] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteAcademyLesson({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy", "structure", courseId] }),
  });

  return (
    <div className="rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.045] p-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">
          {module.title}
          {module.video_url && <span className="ml-2 text-[10px] text-primary">🎬 vídeo</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowEditMod((s) => !s)} className="text-xs text-muted-foreground hover:text-foreground">Editar módulo</button>
          <button onClick={() => setShowAdd((s) => !s)} className="text-xs text-primary">+ Aula</button>
          <button onClick={onDelete} className="text-xs text-destructive">Excluir módulo</button>
        </div>
      </div>
      {showEditMod && (
        <div className="mt-3 grid gap-2 rounded-lg border border-ink/10 bg-background/40 p-3 text-xs">
          <input placeholder="Título" value={modForm.title} onChange={(e) => setModForm({ ...modForm, title: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
          <input placeholder="Resumo curto" value={modForm.summary} onChange={(e) => setModForm({ ...modForm, summary: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
          <textarea placeholder="Descrição" rows={2} value={modForm.description} onChange={(e) => setModForm({ ...modForm, description: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
          <input placeholder="Vídeo de introdução (YouTube/Vimeo)" value={modForm.video_url} onChange={(e) => setModForm({ ...modForm, video_url: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
          <input placeholder="Imagem de capa (URL)" value={modForm.cover_url} onChange={(e) => setModForm({ ...modForm, cover_url: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
          <div className="grid grid-cols-2 gap-2">
            <input type="number" placeholder="Ordem" value={modForm.order_index} onChange={(e) => setModForm({ ...modForm, order_index: Number(e.target.value) })} className="rounded border border-ink/10 bg-background/40 p-2" />
            <label className="flex items-center gap-2"><input type="checkbox" checked={modForm.published} onChange={(e) => setModForm({ ...modForm, published: e.target.checked })} /> Publicado</label>
          </div>
          <div className="rounded border border-ink/10 bg-background/40 p-2">
            <div className="mb-2 flex items-center justify-between">
              <div className="font-semibold">📚 Materiais de estudo do módulo</div>
              <button
                type="button"
                onClick={() => setModForm({ ...modForm, materials: [...(modForm.materials || []), { name: "", url: "", description: "" }] })}
                className="text-primary"
              >
                + adicionar
              </button>
            </div>
            <p className="mb-2 text-[11px] text-muted-foreground">
              Links de aulas complementares, PDFs, planilhas, ferramentas ou vídeos externos que valem para o módulo inteiro.
            </p>
            {(modForm.materials || []).map((t: any, i: number) => (
              <div key={i} className="mb-2 grid gap-1 sm:grid-cols-[1fr_2fr_2fr_auto]">
                <input placeholder="Nome" value={t.name} onChange={(e) => {
                  const arr = [...modForm.materials]; arr[i] = { ...arr[i], name: e.target.value }; setModForm({ ...modForm, materials: arr });
                }} className="rounded border border-ink/10 bg-background/40 p-1" />
                <input placeholder="URL (https://…)" value={t.url} onChange={(e) => {
                  const arr = [...modForm.materials]; arr[i] = { ...arr[i], url: e.target.value }; setModForm({ ...modForm, materials: arr });
                }} className="rounded border border-ink/10 bg-background/40 p-1" />
                <input placeholder="Descrição (opcional)" value={t.description ?? ""} onChange={(e) => {
                  const arr = [...modForm.materials]; arr[i] = { ...arr[i], description: e.target.value }; setModForm({ ...modForm, materials: arr });
                }} className="rounded border border-ink/10 bg-background/40 p-1" />
                <button type="button" onClick={() => setModForm({ ...modForm, materials: modForm.materials.filter((_: any, j: number) => j !== i) })} className="text-destructive">×</button>
              </div>
            ))}
          </div>
          <button onClick={() => saveMod.mutate()} disabled={saveMod.isPending} className="btn-primary text-xs">{saveMod.isPending ? "Salvando…" : "Salvar módulo"}</button>
          {saveMod.error && <div className="text-destructive">{(saveMod.error as Error).message}</div>}
        </div>
      )}
      <ul className="mt-2 divide-y divide-ink/12">
        {module.lessons.map((l: any) => (
          <li key={l.id} className="flex items-center justify-between py-2 text-xs">
            <span>
              {l.title} · <span className="text-muted-foreground">{l.lesson_type} · {l.duration_min}min · +{l.points}pts</span>
              {Array.isArray(l.tools) && l.tools.length > 0 && <span className="ml-1 text-primary">· 🛠 {l.tools.length}</span>}
            </span>
            <button onClick={() => confirm("Excluir aula?") && del.mutate(l.id)} className="text-destructive">Excluir</button>
          </li>
        ))}
      </ul>
      {showAdd && (
        <div className="mt-3 grid gap-2 rounded-lg border border-ink/10 bg-background/40 p-3 text-xs">
          <div className="grid gap-2 sm:grid-cols-2">
            <input placeholder="Slug" value={lesson.slug} onChange={(e) => setLesson({ ...lesson, slug: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
            <input placeholder="Título" value={lesson.title} onChange={(e) => setLesson({ ...lesson, title: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
            <select value={lesson.lesson_type} onChange={(e) => setLesson({ ...lesson, lesson_type: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2">
              <option value="video">Vídeo</option>
              <option value="text">Texto</option>
              <option value="pdf">PDF</option>
              <option value="quiz">Quiz</option>
            </select>
            <input type="number" placeholder="Duração (min)" value={lesson.duration_min} onChange={(e) => setLesson({ ...lesson, duration_min: Number(e.target.value) })} className="rounded border border-ink/10 bg-background/40 p-2" />
            <input placeholder="Vídeo (URL YouTube/Vimeo)" value={lesson.video_url} onChange={(e) => setLesson({ ...lesson, video_url: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2 sm:col-span-2" />
            <input placeholder="PDF (URL)" value={lesson.pdf_url} onChange={(e) => setLesson({ ...lesson, pdf_url: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2 sm:col-span-2" />
            <input type="number" placeholder="Pontos" value={lesson.points} onChange={(e) => setLesson({ ...lesson, points: Number(e.target.value) })} className="rounded border border-ink/10 bg-background/40 p-2" />
            <input type="number" placeholder="Ordem" value={lesson.order_index} onChange={(e) => setLesson({ ...lesson, order_index: Number(e.target.value) })} className="rounded border border-ink/10 bg-background/40 p-2" />
          </div>
          <textarea placeholder="Conteúdo (Markdown)" rows={4} value={lesson.content_md} onChange={(e) => setLesson({ ...lesson, content_md: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2" />
          <div className="rounded border border-ink/10 bg-background/40 p-2">
            <div className="mb-2 flex items-center justify-between">
              <div className="font-semibold">🛠 Ferramentas & links de apoio</div>
              <button
                type="button"
                onClick={() => setLesson({ ...lesson, tools: [...(lesson.tools || []), { name: "", url: "", description: "" }] })}
                className="text-primary"
              >
                + adicionar
              </button>
            </div>
            {(lesson.tools || []).map((t: any, i: number) => (
              <div key={i} className="mb-2 grid gap-1 sm:grid-cols-[1fr_2fr_2fr_auto]">
                <input placeholder="Nome" value={t.name} onChange={(e) => {
                  const arr = [...lesson.tools]; arr[i] = { ...arr[i], name: e.target.value }; setLesson({ ...lesson, tools: arr });
                }} className="rounded border border-ink/10 bg-background/40 p-1" />
                <input placeholder="URL" value={t.url} onChange={(e) => {
                  const arr = [...lesson.tools]; arr[i] = { ...arr[i], url: e.target.value }; setLesson({ ...lesson, tools: arr });
                }} className="rounded border border-ink/10 bg-background/40 p-1" />
                <input placeholder="Descrição (opcional)" value={t.description ?? ""} onChange={(e) => {
                  const arr = [...lesson.tools]; arr[i] = { ...arr[i], description: e.target.value }; setLesson({ ...lesson, tools: arr });
                }} className="rounded border border-ink/10 bg-background/40 p-1" />
                <button type="button" onClick={() => setLesson({ ...lesson, tools: lesson.tools.filter((_: any, j: number) => j !== i) })} className="text-destructive">×</button>
              </div>
            ))}
          </div>
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary text-xs">{save.isPending ? "Salvando…" : "Salvar aula"}</button>
          {save.error && <div className="text-destructive">{(save.error as Error).message}</div>}
        </div>
      )}
    </div>
  );
}

function StudentsTab() {
  const { data } = useQuery({ queryKey: ["adm-academy", "students"], queryFn: () => listAcademyStudents() });
  return (
    <div className="card-glass overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr><th className="py-2">Nome</th><th>Cidade/UF</th><th>WhatsApp</th><th>Pontos</th><th>Cadastro</th></tr>
        </thead>
        <tbody className="divide-y divide-ink/12">
          {(data ?? []).map((s: any) => (
            <tr key={s.id}>
              <td className="py-2">{s.full_name || "—"}</td>
              <td>{[s.city, s.state].filter(Boolean).join(" / ") || "—"}</td>
              <td>{s.phone || "—"}</td>
              <td>{s.totalPoints}</td>
              <td className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString("pt-BR")}</td>
            </tr>
          ))}
          {data && data.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-xs text-muted-foreground">Nenhum aluno ainda.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function OffersTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-academy", "offers"], queryFn: () => listAdminOffers() });
  const [form, setForm] = useState<any>({ title: "", description: "", coupon: "", url: "", audience: "todos", active: true });
  const [editId, setEditId] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const payload: any = { ...form };
      if (editId) payload.id = editId;
      if (!payload.coupon) delete payload.coupon;
      return saveAcademyOffer({ data: payload });
    },
    onSuccess: () => {
      setForm({ title: "", description: "", coupon: "", url: "", audience: "todos", active: true });
      setEditId(null);
      qc.invalidateQueries({ queryKey: ["adm-academy", "offers"] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteAcademyOffer({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy", "offers"] }),
  });
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="card-glass">
        <h2 className="text-lg font-semibold">Ofertas</h2>
        <ul className="mt-3 divide-y divide-ink/12">
          {(data ?? []).map((o: any) => (
            <li key={o.id} className="flex items-center justify-between py-2 text-sm">
              <button onClick={() => { setEditId(o.id); setForm({ title: o.title, description: o.description ?? "", coupon: o.coupon ?? "", url: o.url, audience: o.audience ?? "todos", active: o.active }); }} className="text-left hover:text-primary">
                <div className="font-medium">{o.title}</div>
                <div className="text-xs text-muted-foreground">{o.audience} · {o.active ? "ativa" : "inativa"}</div>
              </button>
              <button onClick={() => confirm("Excluir oferta?") && del.mutate(o.id)} className="text-xs text-destructive">Excluir</button>
            </li>
          ))}
        </ul>
      </div>
      <div className="card-glass space-y-3">
        <h2 className="text-lg font-semibold">{editId ? "Editar oferta" : "Nova oferta"}</h2>
        <input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" />
        <textarea placeholder="Descrição" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" />
        <input placeholder="Cupom (opcional)" value={form.coupon} onChange={(e) => setForm({ ...form, coupon: e.target.value })} className="w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" />
        <input placeholder="URL de destino" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" />
        <input placeholder="Público" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" />
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Ativa</label>
        <div className="flex gap-2">
          <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary text-sm">{save.isPending ? "Salvando…" : "Salvar"}</button>
          {editId && <button onClick={() => { setEditId(null); setForm({ title: "", description: "", coupon: "", url: "", audience: "todos", active: true }); }} className="btn-outline text-xs">Cancelar</button>}
        </div>
        {save.error && <span className="text-xs text-destructive">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}

function HelpTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-academy", "help"], queryFn: () => listAdminHelpArticles() });
  const [form, setForm] = useState<any>({ slug: "", title: "", category: "Geral", body_md: "", published: true });
  const [editId, setEditId] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => saveHelpArticle({ data: editId ? { ...form, id: editId } : form }),
    onSuccess: () => {
      setForm({ slug: "", title: "", category: "Geral", body_md: "", published: true });
      setEditId(null);
      qc.invalidateQueries({ queryKey: ["adm-academy", "help"] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteHelpArticle({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy", "help"] }),
  });
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="card-glass">
        <h2 className="text-lg font-semibold">Artigos</h2>
        <ul className="mt-3 divide-y divide-ink/12">
          {(data ?? []).map((a: any) => (
            <li key={a.id} className="flex items-center justify-between py-2 text-sm">
              <button onClick={() => { setEditId(a.id); setForm({ slug: a.slug, title: a.title, category: a.category ?? "Geral", body_md: a.body_md ?? "", published: a.published }); }} className="text-left hover:text-primary">
                <div className="font-medium">{a.title}</div>
                <div className="text-xs text-muted-foreground">{a.category} · {a.published ? "publicado" : "rascunho"}</div>
              </button>
              <button onClick={() => confirm("Excluir artigo?") && del.mutate(a.id)} className="text-xs text-destructive">Excluir</button>
            </li>
          ))}
        </ul>
      </div>
      <div className="card-glass space-y-3">
        <h2 className="text-lg font-semibold">{editId ? "Editar artigo" : "Novo artigo"}</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2 text-sm" />
          <input placeholder="Categoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2 text-sm" />
          <input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-2 text-sm sm:col-span-2" />
        </div>
        <textarea placeholder="Conteúdo (Markdown)" rows={8} value={form.body_md} onChange={(e) => setForm({ ...form, body_md: e.target.value })} className="w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-sm" />
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publicado</label>
        <button onClick={() => save.mutate()} disabled={save.isPending} className="btn-primary text-sm">{save.isPending ? "Salvando…" : "Salvar"}</button>
        {save.error && <span className="text-xs text-destructive">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}

function TicketsTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-academy", "tickets"], queryFn: () => listAdminTickets() });
  const update = useMutation({
    mutationFn: (input: { id: string; status?: any; admin_notes?: string }) => updateAdminTicket({ data: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy", "tickets"] }),
  });
  return (
    <div className="space-y-3">
      {(data ?? []).map((t: any) => (
        <div key={t.id} className="card-glass">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-semibold">{t.subject}</div>
              <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("pt-BR")} · WhatsApp: {t.whatsapp || "—"}</div>
            </div>
            <select value={t.status} onChange={(e) => update.mutate({ id: t.id, status: e.target.value })} className="rounded border border-ink/10 bg-background/40 p-1 text-xs">
              <option value="open">Aberto</option>
              <option value="in_progress">Em andamento</option>
              <option value="resolved">Resolvido</option>
              <option value="closed">Fechado</option>
            </select>
          </div>
          <p className="mt-2 whitespace-pre-wrap text-sm">{t.message}</p>
          <textarea
            defaultValue={t.admin_notes ?? ""}
            onBlur={(e) => e.target.value !== (t.admin_notes ?? "") && update.mutate({ id: t.id, admin_notes: e.target.value })}
            placeholder="Notas internas (salva ao sair do campo)"
            rows={2}
            className="mt-3 w-full rounded-lg border border-ink/10 bg-background/40 p-2 text-xs"
          />
        </div>
      ))}
      {data && data.length === 0 && <div className="card-glass text-sm text-muted-foreground">Nenhum chamado.</div>}
    </div>
  );
}

function SubmissionsTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adm-academy", "submissions"], queryFn: () => listAdminSubmissions() });
  const review = useMutation({
    mutationFn: (input: any) => reviewSubmission({ data: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adm-academy", "submissions"] }),
  });
  return (
    <div className="space-y-3">
      {(data ?? []).map((s: any) => (
        <div key={s.id} className="card-glass">
          <div className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString("pt-BR")} · status: <strong>{s.status}</strong></div>
          {s.content && <p className="mt-2 whitespace-pre-wrap text-sm">{s.content}</p>}
          {s.link_url && <a href={s.link_url} target="_blank" rel="noopener" className="mt-1 block text-xs text-primary underline">{s.link_url}</a>}
          {s.file_url && <a href={s.file_url} target="_blank" rel="noopener" className="mt-1 block text-xs text-primary underline">Arquivo</a>}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button onClick={() => review.mutate({ id: s.id, status: "approved", feedback: s.feedback ?? "" })} className="btn-primary text-xs">Aprovar</button>
            <button onClick={() => review.mutate({ id: s.id, status: "rejected", feedback: s.feedback ?? "" })} className="btn-outline text-xs">Rejeitar</button>
            <input placeholder="Nota (0-10)" defaultValue={s.grade ?? ""} onBlur={(e) => { const v = Number(e.target.value); if (!Number.isNaN(v)) review.mutate({ id: s.id, status: s.status, grade: v }); }} className="w-24 rounded border border-ink/10 bg-background/40 p-1 text-xs" />
            <input placeholder="Feedback" defaultValue={s.feedback ?? ""} onBlur={(e) => e.target.value !== (s.feedback ?? "") && review.mutate({ id: s.id, status: s.status, feedback: e.target.value })} className="flex-1 rounded border border-ink/10 bg-background/40 p-1 text-xs" />
          </div>
        </div>
      ))}
      {data && data.length === 0 && <div className="card-glass text-sm text-muted-foreground">Nenhuma entrega.</div>}
    </div>
  );
}